import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import PetsIcon from '@mui/icons-material/Pets';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import dayjs from 'dayjs';
import { useGetPets } from 'hooks/pets/useGetPets';
import React, { useMemo, useState } from 'react';
import { ServiceType } from '../../../../happypaws-shared/src/Services/services';
import { SitterProfileResponse } from '../../../../happypaws-shared/src/Sitter/models/SitterProfileResponse';

interface BookingModalProps {
  open: boolean;
  sitter: SitterProfileResponse;
  onClose: () => void;
  onConfirm: (bookingData: BookingFormData) => void;
  isLoading?: boolean;
}

export interface BookingFormData {
  sitterId: string;
  serviceType: ServiceType;
  startDate: string;
  endDate: string;
  petIds: string[];
  notes: string | null;
  totalPrice: number;
}

const BookingModal: React.FC<BookingModalProps> = ({
  open,
  sitter,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { data: pets, isLoading: isPetsLoading } = useGetPets();

  const [serviceType, setServiceType] = useState<ServiceType>(
    sitter.serviceTypes?.[0] ?? ServiceType.PET_SITTING
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>([]);
  const [notes, setNotes] = useState<string | null>(null);

  const selectedPricing = useMemo(
    () => sitter.pricing.find((p) => p.service === serviceType),
    [sitter.pricing, serviceType]
  );

  const numberOfNights = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const diff = end.diff(start, 'day');
    return diff > 0 ? diff : 0;
  }, [startDate, endDate]);

  const totalPrice = useMemo(() => {
    if (!selectedPricing || numberOfNights === 0) return 0;
    return selectedPricing.priceMin * numberOfNights;
  }, [selectedPricing, numberOfNights]);

  const handlePetToggle = (petId: string) => {
    setSelectedPetIds((prev) =>
      prev.includes(petId) ? prev.filter((id) => id !== petId) : [...prev, petId]
    );
  };

  const handleClose = () => {
    setServiceType(sitter.serviceTypes?.[0] ?? ServiceType.PET_SITTING);
    setStartDate('');
    setEndDate('');
    setSelectedPetIds([]);
    setNotes(null);
    onClose();
  };

  const isValid =
    serviceType && startDate && endDate && numberOfNights > 0 && selectedPetIds.length > 0;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({
      sitterId: sitter.id,
      serviceType,
      startDate,
      endDate,
      petIds: selectedPetIds,
      notes: notes?.trim() || null,
      totalPrice,
    });
  };

  const formatServiceName = (service: string) =>
    service.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            Book {sitter.firstName}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Fill in the details to send a booking request
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Service Type */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Service type
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value as ServiceType)}
              sx={{ borderRadius: 2 }}
            >
              {sitter.serviceTypes?.map((s) => (
                <MenuItem key={s} value={s}>
                  {formatServiceName(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Dates */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Start date
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (endDate && e.target.value >= endDate) setEndDate('');
              }}
              inputProps={{ min: dayjs().format('YYYY-MM-DD') }}
              slotProps={{
                input: {
                  startAdornment: (
                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: '#178B8E' }} />
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              End date
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              inputProps={{ min: startDate || dayjs().format('YYYY-MM-DD') }}
              slotProps={{
                input: {
                  startAdornment: (
                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: '#178B8E' }} />
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        </Box>

        {/* Pet Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Select pet(s)
          </Typography>
          {isPetsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#178B8E' }} />
            </Box>
          ) : !pets || pets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pets found. Add a pet first.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pets.map((pet) => {
                const selected = selectedPetIds.includes(pet.id!);
                return (
                  <Paper
                    key={pet.id}
                    onClick={() => handlePetToggle(pet.id!)}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      border: selected ? '2px solid #178B8E' : '1px solid #E0E0E0',
                      borderRadius: 2,
                      backgroundColor: selected ? 'rgba(23,139,142,0.04)' : 'white',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Avatar
                      src={pet.photoUrl ?? undefined}
                      sx={{ width: 36, height: 36, backgroundColor: '#178B8E' }}
                    >
                      <PetsIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {pet.petName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pet.breed ?? pet.type}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={selected}
                      size="small"
                      sx={{ color: '#178B8E', '&.Mui-checked': { color: '#178B8E' } }}
                    />
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Notes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Notes (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Any special instructions, medications, feeding preferences..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        {/* Price Breakdown */}
        {selectedPricing && numberOfNights > 0 && (
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(23,139,142,0.04)',
              border: '1px solid rgba(23,139,142,0.2)',
              mb: 3,
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Price breakdown
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                ${selectedPricing.priceMin} / night × {numberOfNights} night
                {numberOfNights > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ${totalPrice}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="body1" fontWeight={700} color="#178B8E">
                ${totalPrice}
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Confirm Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!isValid || isLoading}
          onClick={handleConfirm}
          sx={{
            backgroundColor: '#178B8E',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.5,
            '&:hover': { backgroundColor: '#0F6164' },
          }}
        >
          {isLoading ? (
            <CircularProgress size={22} sx={{ color: 'white' }} />
          ) : (
            'Send Booking Request'
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
